import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { todayLocal, useTodayLocal } from "@/lib/utils";
import type {
    AccountsResponse,
    ContributionCreatePayload,
    ContributionUpdatePayload,
    ExpensesResponse,
    GoalCreatePayload,
    GoalDetail,
    GoalStatus,
    GoalUpdatePayload,
    GoalsResponse,
    IncomeCategoriesResponse,
    ManagedCategoriesResponse,
    CategoryCreatePayload,
    CategoryUpdatePayload,
    IncomeCreatePayload,
    IncomeUpdatePayload,
    IncomesResponse,
    MeResponse,
    ReferencesResponse,
    SnapshotCreatePayload,
    SnapshotUpdatePayload,
    SnapshotsResponse,
    TransactionCreatePayload,
    TransactionUpdatePayload,
    TransactionsResponse,
    DashboardResponse,
    BudgetsResponse,
    BudgetCreatePayload,
    BudgetUpdatePayload,
    BudgetRecommendationsResponse,
    BudgetRecommendation,
    BudgetArchetypesResponse,
    BudgetSettingsPatch,
    InvestmentsResponse,
    InvestmentSettingsPayload,
    InstanceConfig, CurrenciesResponse, CurrencyCreatePayload, CurrencyUpdatePayload,
    ImbalancePreview,   // SPEC-050
    AccountCreatePayload, AccountUpdatePayload, AccountUsage,
    NoteCleanupCandidates, NoteCleanupApplyResult, NoteCleanupRunsResponse,
    PlanResponse,
    PlanSettings,
} from "./types";

export function useMe() {
    return useQuery({
        queryKey: ["me"],
        queryFn: () => apiFetch<MeResponse>("/v1/web/me"),
        retry: false,
        staleTime: 5 * 60_000,
    });
}

export function useReferences() {
    return useQuery({
        queryKey: ["references"],
        queryFn: () => apiFetch<ReferencesResponse>("/v1/web/references"),
        staleTime: 5 * 60_000,
    });
}

export function useExpenses() {
    return useQuery({
        queryKey: ["expenses"],
        queryFn: () => apiFetch<ExpensesResponse>("/v1/web/expenses?limit=20000"),
        staleTime: 60_000,
    });
}

export function useAccounts() {
    // SPEC-024: «сегодня» = локальный день клиента (asOf балансов «сейчас»), не UTC.
    // В queryKey → рефетч на локальной полуночи (балансы катятся в новый день).
    const today = todayLocal();
    return useQuery({
        queryKey: ["accounts", today],
        queryFn: () => apiFetch<AccountsResponse>(`/v1/web/accounts?today=${today}`),
        staleTime: 30_000,
    });
}

export function useSnapshots() {
    return useQuery({
        queryKey: ["snapshots"],
        queryFn: () => apiFetch<SnapshotsResponse>("/v1/web/snapshots?limit=20000"),
        staleTime: 30_000,
    });
}

// ADM-01 (SPEC-042): любая мутация, двигающая деньги (снапшот/доход/цель/взнос/обмен),
// обязана инвалидировать финансовые агрегаты — дашборд и сводку счетов. Иначе открытый
// дашборд (refetchOnWindowFocus=false) показывает цифры «до правки» до ручного Refresh.
function invalidateMoneyAggregates(qc: ReturnType<typeof useQueryClient>) {
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["accounts"] });
}

/** SPEC-026: снапшот инвест-ведра = доход стейкинга, поэтому портфель тоже. */
function invalidateOnSnapshotMutation(qc: ReturnType<typeof useQueryClient>) {
    qc.invalidateQueries({ queryKey: ["snapshots"] });
    qc.invalidateQueries({ queryKey: ["investments"] });
    invalidateMoneyAggregates(qc);
}

export function useCreateSnapshot() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: SnapshotCreatePayload) =>
            apiFetch<{ ok: true; id: string; inserted: boolean }>("/v1/web/snapshots", {
                method: "POST",
                body: JSON.stringify(payload),
            }),
        onSuccess: () => invalidateOnSnapshotMutation(qc),
    });
}

export function useUpdateSnapshot() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: SnapshotUpdatePayload }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/snapshots/${id}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: () => invalidateOnSnapshotMutation(qc),
    });
}

export function useDeleteSnapshot() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            apiFetch<{ ok: true; deleted: boolean }>(`/v1/web/snapshots/${id}`, { method: "DELETE" }),
        onSuccess: () => invalidateOnSnapshotMutation(qc),
    });
}

export function useIncomeCategories() {
    return useQuery({
        queryKey: ["income-categories"],
        queryFn: () => apiFetch<IncomeCategoriesResponse>("/v1/web/income-categories"),
        staleTime: 5 * 60_000,
    });
}

export function useIncomes() {
    return useQuery({
        queryKey: ["incomes"],
        queryFn: () => apiFetch<IncomesResponse>("/v1/web/incomes?limit=20000"),
        staleTime: 30_000,
    });
}

function invalidateOnIncomeMutation(qc: ReturnType<typeof useQueryClient>) {
    // Доход — это «событие» для счёта (snapshots.ts: incomes +amount): влияет на
    // effective_balance/events_count на «Счетах» и на KPI дохода/нормы дашборда (ADM-01).
    qc.invalidateQueries({ queryKey: ["incomes"] });
    invalidateMoneyAggregates(qc);
}

export function useCreateIncome() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: IncomeCreatePayload) =>
            apiFetch<{ ok: true; id: string; inserted: boolean }>("/v1/web/incomes", {
                method: "POST",
                body: JSON.stringify(payload),
            }),
        onSuccess: (_d, payload) => {
            invalidateOnIncomeMutation(qc);
            // Если income привязан к цели — обновляем goal balance.
            if (payload.goal_id) {
                qc.invalidateQueries({ queryKey: ["goals"] });
                qc.invalidateQueries({ queryKey: ["goal", payload.goal_id] });
            }
        },
    });
}

export function useUpdateIncome() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: IncomeUpdatePayload }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/incomes/${id}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: () => {
            invalidateOnIncomeMutation(qc);
            // Goal attachment изменился или amount/account/date — пересчитать balance во
            // всех goals + любой открытый goal-detail. Префикс ["goal"] покрывает и отвязку
            // goal_id: null (ADM-16: точечная инвалидация по truthy goal_id теряла старую цель).
            qc.invalidateQueries({ queryKey: ["goals"] });
            qc.invalidateQueries({ queryKey: ["goal"] });
        },
    });
}

export function useDeleteIncome() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            apiFetch<{ ok: true; deleted: boolean }>(`/v1/web/incomes/${id}`, { method: "DELETE" }),
        onSuccess: () => {
            invalidateOnIncomeMutation(qc);
            qc.invalidateQueries({ queryKey: ["goals"] });
            qc.invalidateQueries({ queryKey: ["goal"] });   // ADM-16: открытый detail цели удалённого goal-дохода
        },
    });
}

// ── Goals ──────────────────────────────────────────────────────────────────

// SPEC-024/SPEC-054 AC19: цели запрашиваются с той же локальной датой, что счета и
// дашборд. Без неё около полуночи targeted на «Целях» считался по UTC-дню, а на
// «Счетах» — по локальному, и одно и то же число на двух экранах расходилось.
export function useGoals(status: GoalStatus | "all" = "active") {
    const today = todayLocal();
    return useQuery({
        queryKey: ["goals", status, today],
        queryFn: () => apiFetch<GoalsResponse>(`/v1/web/goals?status=${status}&today=${today}`),
        staleTime: 30_000,
    });
}

export function useGoalDetail(id: string | undefined) {
    const today = todayLocal();
    return useQuery({
        queryKey: ["goal", id, today],
        queryFn: () => apiFetch<GoalDetail>(`/v1/web/goals/${id}?today=${today}`),
        enabled: !!id,
        staleTime: 30_000,
    });
}

/** Список целей + карточка (goalId опущен → все открытые карточки) + деньги:
 *  любое движение по цели меняет targeted, а значит и free. */
function invalidateOnGoalMutation(qc: ReturnType<typeof useQueryClient>, goalId?: string) {
    qc.invalidateQueries({ queryKey: ["goals"] });
    qc.invalidateQueries({ queryKey: goalId ? ["goal", goalId] : ["goal"] });
    invalidateMoneyAggregates(qc);
}

export function useCreateGoal() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: GoalCreatePayload) =>
            apiFetch<{ ok: true; id: string; inserted: boolean }>("/v1/web/goals", {
                method: "POST",
                body: JSON.stringify(payload),
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["goals"] });
            invalidateMoneyAggregates(qc);   // targeted → free (дашборд + сводка счетов)
        },
    });
}

export function useUpdateGoal() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: GoalUpdatePayload }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/goals/${id}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: (_d, { id }) => invalidateOnGoalMutation(qc, id),
    });
}

export function useSetGoalStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, status }: { id: string; status: GoalStatus }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/goals/${id}/status`, {
                method: "POST",
                body: JSON.stringify({ status }),
            }),
        // Архив/достижение цели меняет targeted → free.
        onSuccess: (_d, { id }) => invalidateOnGoalMutation(qc, id),
    });
}

export function useDeleteGoal() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            apiFetch<{ ok: true; deleted: boolean }>(`/v1/web/goals/${id}`, { method: "DELETE" }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["goals"] });
            qc.invalidateQueries({ queryKey: ["incomes"] });
            invalidateMoneyAggregates(qc);
        },
    });
}

export function useCreateContribution() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: ContributionCreatePayload) =>
            apiFetch<{ ok: true; id: string; inserted: boolean }>("/v1/web/goal-contributions", {
                method: "POST",
                body: JSON.stringify(payload),
            }),
        // Взнос меняет targeted → free.
        onSuccess: (_d, payload) => invalidateOnGoalMutation(qc, payload.goal_id),
    });
}

export function useUpdateContribution() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: ContributionUpdatePayload }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/goal-contributions/${id}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: () => invalidateOnGoalMutation(qc),
    });
}

export function useDeleteContribution() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            apiFetch<{ ok: true; deleted: boolean }>(`/v1/web/goal-contributions/${id}`, { method: "DELETE" }),
        onSuccess: () => invalidateOnGoalMutation(qc),
    });
}

// ── Transactions / chains ──────────────────────────────────────────────────

export function useTransactions() {
    return useQuery({
        queryKey: ["transactions"],
        queryFn: () => apiFetch<TransactionsResponse>("/v1/web/transactions?limit=20000"),
        staleTime: 30_000,
    });
}

function invalidateOnTxMutation(qc: ReturnType<typeof useQueryClient>) {
    qc.invalidateQueries({ queryKey: ["transactions"] });
    qc.invalidateQueries({ queryKey: ["snapshots"] });
    qc.invalidateQueries({ queryKey: ["accounts"] });
    qc.invalidateQueries({ queryKey: ["investments"] });   // SPEC-026: покупка USDT→ETH меняет позицию
    qc.invalidateQueries({ queryKey: ["dashboard"] });     // invested/free
}

export function useCreateTransaction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: TransactionCreatePayload) =>
            apiFetch<{ ok: true; id: string; inserted: boolean; snapshot_ids: string[] }>("/v1/web/transactions", {
                method: "POST",
                body: JSON.stringify(payload),
            }),
        onSuccess: () => { invalidateOnTxMutation(qc); },
    });
}

export function useUpdateTransaction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: TransactionUpdatePayload }) =>
            apiFetch<{ ok: true; updated: boolean; new_snapshot_ids?: string[] }>(`/v1/web/transactions/${id}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: () => { invalidateOnTxMutation(qc); qc.invalidateQueries({ queryKey: ["goals"] }); qc.invalidateQueries({ queryKey: ["goal"] }); },
    });
}

export function useDeleteTransaction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            apiFetch<{ ok: true; deleted: boolean; deleted_snapshots: number }>(`/v1/web/transactions/${id}`, { method: "DELETE" }),
        // ADM-16: удаление goal-tagged обмена меняет goal balance — как в useUpdateTransaction.
        onSuccess: () => { invalidateOnTxMutation(qc); qc.invalidateQueries({ queryKey: ["goals"] }); qc.invalidateQueries({ queryKey: ["goal"] }); },
    });
}

// ── Dashboard (SPEC-013) ────────────────────────────────────────────────────

type RangeParams = { from?: string; to?: string };

/** Окно from/to + `today` (SPEC-024: локальный день клиента, не UTC) → query-string. */
function rangeQuery(params: RangeParams | undefined, today: string): string {
    const qs = new URLSearchParams();
    if (params?.from) qs.set("from", params.from);
    if (params?.to) qs.set("to", params.to);
    qs.set("today", today);
    return qs.toString();
}

export function useDashboard(params?: RangeParams) {
    const today = todayLocal();
    const qs = rangeQuery(params, today);
    return useQuery({
        queryKey: ["dashboard", params?.from ?? null, params?.to ?? null, today],
        queryFn: () => apiFetch<DashboardResponse>(`/v1/web/dashboard?${qs}`),
        staleTime: 30_000,
    });
}

// ── Budgets (SPEC-020) ───────────────────────────────────────────────────────

export function useBudgets() {
    // Локальный день, как у usePlan: иначе на границе месяца карточка потолка (/plan,
    // локальный месяц) и бюджеты (UTC-месяц воркера) показывали бы разные месяцы.
    // Состояние, а не todayLocal() на рендере — иначе ключ не обновится через полночь.
    const today = useTodayLocal();
    return useQuery({
        queryKey: ["budgets", today],
        queryFn: () => apiFetch<BudgetsResponse>(`/v1/web/budgets?today=${today}`),
        staleTime: 30_000,
    });
}

// ADM-07 (SPEC-042): ручной CRUD лимита обязан рефетчить и рекомендации RBAR —
// карточка рекомендации показывает current_limit/дельту относительно лимита.
// SPEC-059: мутации входов аллокации несут `?today=` локального дня (как GET /budgets) —
// сверка после мутации иначе считала бы разбивку за UTC-месяц воркера, вечером
// последнего числа в UTC+2 это соседний месяц.
export function useCreateBudget() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: BudgetCreatePayload) =>
            apiFetch<{ ok: true; id: string; inserted: boolean }>(`/v1/web/budgets?today=${todayLocal()}`, {
                method: "POST",
                body: JSON.stringify(payload),
            }),
        onSuccess: () => invalidateAdaptive(qc),
    });
}

export function useUpdateBudget() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: BudgetUpdatePayload }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/budgets/${id}?today=${todayLocal()}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: () => invalidateAdaptive(qc),
    });
}

export function useDeleteBudget() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            apiFetch<{ ok: true; deleted: boolean }>(`/v1/web/budgets/${id}?today=${todayLocal()}`, { method: "DELETE" }),
        onSuccess: () => invalidateAdaptive(qc),
    });
}

// ── Финансовый план (SPEC-057) ──────────────────────────────────────────────
// Отдельный запрос, а не поле дашборда: на воркере план тоже живёт отдельным
// endpoint'ом, чтобы не утяжелять getDashboard на каждом входе в Admin.

export function usePlan() {
    // Дата локальная, а не UTC: вечером 31-го числа в UTC+2 сервер иначе считал бы
    // потолок за следующий месяц (та же причина, что у `?today=` в SPEC-024).
    const today = useTodayLocal();
    return useQuery({
        queryKey: ["plan", today],
        queryFn: () => apiFetch<PlanResponse>(`/v1/web/plan?today=${today}`),
        staleTime: 60_000,
    });
}

function invalidatePlan(qc: ReturnType<typeof useQueryClient>) {
    qc.invalidateQueries({ queryKey: ["plan"] });
    // Конверт снимает категорию с RBAR-контура — рекомендации и бюджеты меняются вместе с планом.
    qc.invalidateQueries({ queryKey: ["budgets"] });
    qc.invalidateQueries({ queryKey: ["budget-recommendations"] });
}

export function useUpdatePlanSettings() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: Partial<PlanSettings>) =>
            apiFetch<{ ok: true; settings: PlanSettings }>(`/v1/web/plan/settings?today=${todayLocal()}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: () => invalidatePlan(qc),
    });
}

export function useCreatePlanEnvelope() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: { category_id: string; annual_target_eur: number; cycle_start_month?: string; priority?: number }) =>
            apiFetch<{ ok: true; id: string; inserted: boolean }>(`/v1/web/plan/envelopes?today=${todayLocal()}`, {
                method: "POST",
                body: JSON.stringify({ id: crypto.randomUUID(), ...payload }),
            }),
        onSuccess: () => invalidatePlan(qc),
    });
}

export function useUpdatePlanEnvelope() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: { annual_target_eur?: number; priority?: number; cycle_start_month?: string } }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/plan/envelopes/${id}?today=${todayLocal()}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: () => invalidatePlan(qc),
    });
}

export function useDeletePlanEnvelope() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            apiFetch<{ ok: true; deleted: boolean }>(`/v1/web/plan/envelopes/${id}?today=${todayLocal()}`, { method: "DELETE" }),
        onSuccess: () => invalidatePlan(qc),
    });
}

// ── Adaptive budgets RBAR (SPEC-023) — advisory ──────────────────────────────

export function useBudgetRecommendations() {
    // Локальный месяц, как у useBudgets: иначе на границе месяца рекомендации (UTC-период
    // воркера) и /budgets?today= считали бы потолок и лимиты на разные месяцы.
    const period = useTodayLocal().slice(0, 7);
    return useQuery({
        queryKey: ["budget-recommendations", period],
        queryFn: () => apiFetch<BudgetRecommendationsResponse>(`/v1/web/budgets/recommendations?period=${period}`),
        staleTime: 60_000,
    });
}

export function useBudgetArchetypes() {
    // Тот же локальный месяц, что у рекомендаций: окно классификации и окно рекомендаций
    // на границе месяца иначе расходились бы (клиент уже в новом месяце, воркер в UTC — нет).
    const period = useTodayLocal().slice(0, 7);
    return useQuery({
        queryKey: ["budget-archetypes", period],
        queryFn: () => apiFetch<BudgetArchetypesResponse>(`/v1/web/budgets/archetypes?period=${period}`),
        staleTime: 60_000,
    });
}

function invalidateAdaptive(qc: ReturnType<typeof useQueryClient>) {
    qc.invalidateQueries({ queryKey: ["budgets"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["budget-recommendations"] });
    qc.invalidateQueries({ queryKey: ["budget-archetypes"] });
    // SPEC-060: мутация лимита/настроек запускает сверку, а та может завести авто-конверт —
    // список конвертов и потолок на /plan обязаны перечитаться.
    qc.invalidateQueries({ queryKey: ["plan"] });
}

/**
 * Применить рекомендацию (advisory G5): создаёт/обновляет ручной лимит SPEC-020
 * + логирует решение. Сама система лимит не двигает — это явное действие.
 */
export function useApplyRecommendation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ rec, period }: { rec: BudgetRecommendation; period: string }) => {
            const limit = rec.recommended_limit_eur;
            if (limit == null || limit <= 0) throw new Error("Нет рекомендованного лимита для применения");
            // `?today=` — как у useCreateBudget/useUpdateBudget: сверка после мутации иначе
            // считала бы разбивку за UTC-месяц воркера.
            if (rec.budget_id) {
                await apiFetch(`/v1/web/budgets/${rec.budget_id}?today=${todayLocal()}`, { method: "PUT", body: JSON.stringify({ limit_eur: limit }) });
            } else {
                await apiFetch(`/v1/web/budgets?today=${todayLocal()}`, { method: "POST", body: JSON.stringify({ scope: "category", category_id: rec.category_id, limit_eur: limit }) });
            }
            await apiFetch(`/v1/web/budgets/recommendations/decision`, {
                method: "POST",
                body: JSON.stringify({
                    category_id: rec.category_id, period, archetype: rec.archetype,
                    prev_limit_eur: rec.current_limit_eur, reco_limit_eur: limit,
                    reason_code: rec.reason_code ?? "TRACKING_DOWN", decision: "accepted",
                }),
            });
        },
        onSuccess: () => invalidateAdaptive(qc),
    });
}

export function useDismissRecommendation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ rec, period }: { rec: BudgetRecommendation; period: string }) =>
            apiFetch(`/v1/web/budgets/recommendations/decision`, {
                method: "POST",
                body: JSON.stringify({
                    category_id: rec.category_id, period, archetype: rec.archetype,
                    prev_limit_eur: rec.current_limit_eur, reco_limit_eur: rec.recommended_limit_eur ?? 0,
                    reason_code: rec.reason_code ?? "HOLD", decision: "dismissed",
                }),
            }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["budget-recommendations"] }),
    });
}

export function useUpdateBudgetSettings() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ categoryId, patch }: { categoryId: string; patch: BudgetSettingsPatch }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/budgets/settings/${categoryId}?today=${todayLocal()}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: () => invalidateAdaptive(qc),
    });
}

// ── Investments / крипто-портфель (SPEC-026) ────────────────────────────────

export function useInvestments(params?: RangeParams) {
    const today = todayLocal();
    const qs = rangeQuery(params, today);   // SPEC-029: from/to — окно value_series
    return useQuery({
        queryKey: ["investments", params?.from ?? null, params?.to ?? null, today],
        queryFn: () => apiFetch<InvestmentsResponse>(`/v1/web/investments?${qs}`),
        staleTime: 30_000,
    });
}

export function useUpdateInvestmentSettings() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ accountId, patch }: { accountId: string; patch: InvestmentSettingsPayload }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/investments/settings/${accountId}`, {
                method: "PUT",
                body: JSON.stringify(patch),
            }),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["investments"] }); },
    });
}

// ── Category management (SPEC-017) ──────────────────────────────────────────

type CategoryKind = "expense" | "income";
const catPath = (kind: CategoryKind) => kind === "expense" ? "/v1/web/categories" : "/v1/web/income-categories";

export function useManagedCategories(kind: CategoryKind) {
    return useQuery({
        queryKey: ["managed-categories", kind],
        queryFn: () => apiFetch<ManagedCategoriesResponse>(`${catPath(kind)}?include_inactive=1`),
        staleTime: 30_000,
    });
}

function invalidateCategories(qc: ReturnType<typeof useQueryClient>, kind: CategoryKind) {
    qc.invalidateQueries({ queryKey: ["managed-categories", kind] });
    if (kind === "expense") {
        qc.invalidateQueries({ queryKey: ["references"] });   // refs.categories — Expenses/Dashboard
        qc.invalidateQueries({ queryKey: ["expenses"] });
        qc.invalidateQueries({ queryKey: ["budgets"] });      // ADM-16: подписи/эмодзи категорий на /budgets
    } else {
        qc.invalidateQueries({ queryKey: ["income-categories"] });
        qc.invalidateQueries({ queryKey: ["incomes"] });
    }
}

export function useCreateCategory(kind: CategoryKind) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: CategoryCreatePayload) =>
            apiFetch<{ ok: true; id: string; inserted: boolean }>(catPath(kind), { method: "POST", body: JSON.stringify(payload) }),
        onSuccess: () => invalidateCategories(qc, kind),
    });
}

export function useUpdateCategory(kind: CategoryKind) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: CategoryUpdatePayload }) =>
            apiFetch<{ ok: true; updated: boolean }>(`${catPath(kind)}/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
        onSuccess: () => invalidateCategories(qc, kind),
    });
}


// ── Справочники: конфиг, валюты, вёдра (SPEC-053) ───────────────────────────

export function useInstanceConfig() {
    return useQuery({
        queryKey: ["instance-config"],
        queryFn: () => apiFetch<InstanceConfig>("/v1/web/config"),
        // Конфиг инстанса меняется вместе с деплоем, не по ходу сессии. Исключение —
        // режим оценки фондов (SPEC-054): его переключают прямо из настроек, поэтому
        // мутация ниже инвалидирует этот ключ явно, не дожидаясь staleTime.
        staleTime: 10 * 60_000,
    });
}

/**
 * SPEC-054: переключение режима оценки целевых фондов. Меняет цифры на всех
 * денежных экранах сразу, поэтому после успеха инвалидируются и конфиг, и цели,
 * и агрегаты — иначе открытая вкладка десять минут показывала бы прежний режим.
 */
/**
 * SPEC-050: предпросмотр невязки до сохранения снапшота — «расчётно X, вводишь Y,
 * разница Z». Запрос идёт только когда форма заполнена; `excludeId` при
 * редактировании обязателен, иначе снапшот станет baseline сам себе и невязка
 * всегда выйдет нулевой.
 */
export function useImbalancePreview(params: {
    accountId: string; date: string; amount: number | null; excludeId?: string; enabled?: boolean;
}) {
    const { accountId, date, amount, excludeId, enabled = true } = params;
    const ready = enabled && !!accountId && !!date && amount != null && Number.isFinite(amount);
    return useQuery({
        queryKey: ["imbalance-preview", accountId, date, amount, excludeId ?? null],
        queryFn: () => {
            const qs = new URLSearchParams({ account_id: accountId, date, amount: String(amount) });
            if (excludeId) qs.set("exclude_id", excludeId);
            return apiFetch<ImbalancePreview>(`/v1/web/snapshots/preview?${qs}`);
        },
        enabled: ready,
        staleTime: 10_000,
    });
}

/** SPEC-050: дата доверия невязкам. Меняет и состав журнала, и агрегаты дашборда,
 *  поэтому после успеха инвалидируется всё денежное. */
export function useSetImbalanceTrustFrom() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (value: string | null) =>
            apiFetch<{ ok: true; value: string | null }>("/v1/web/config/imbalance-trust-from", {
                method: "PUT",
                body: JSON.stringify({ value }),
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["instance-config"] });
            qc.invalidateQueries({ queryKey: ["snapshots"] });
            invalidateMoneyAggregates(qc);
        },
    });
}

export function useSetGoalValuationMode() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (markToMarket: boolean) =>
            apiFetch<{ ok: true; mark_to_market: boolean }>("/v1/web/config/goal-funds-valuation", {
                method: "PUT",
                body: JSON.stringify({ mark_to_market: markToMarket }),
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["instance-config"] });
            qc.invalidateQueries({ queryKey: ["goals"] });
            qc.invalidateQueries({ queryKey: ["goal"] });
            invalidateMoneyAggregates(qc);
        },
    });
}

export function useCurrencies() {
    return useQuery({
        queryKey: ["currencies"],
        queryFn: () => apiFetch<CurrenciesResponse>("/v1/web/currencies"),
        staleTime: 60_000,
    });
}

/** Справочники протекают почти во все экраны — инвалидируем широко, но точечно. */
function invalidateReferences(qc: ReturnType<typeof useQueryClient>) {
    for (const key of ["currencies", "references", "accounts", "dashboard", "snapshots", "investments"]) {
        qc.invalidateQueries({ queryKey: [key] });
    }
}

export function useCreateCurrency() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: CurrencyCreatePayload) =>
            apiFetch<{ ok: true; code: string; inserted: boolean }>("/v1/web/currencies", { method: "POST", body: JSON.stringify(payload) }),
        onSuccess: () => invalidateReferences(qc),
    });
}

export function useUpdateCurrency() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ code, patch }: { code: string; patch: CurrencyUpdatePayload }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/currencies/${code}`, { method: "PATCH", body: JSON.stringify(patch) }),
        onSuccess: () => invalidateReferences(qc),
    });
}

export function useCreateAccount() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: AccountCreatePayload) =>
            apiFetch<{ ok: true; id: string; inserted: boolean }>("/v1/web/accounts", { method: "POST", body: JSON.stringify(payload) }),
        onSuccess: () => invalidateReferences(qc),
    });
}

export function useUpdateAccount() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }: { id: string; patch: AccountUpdatePayload }) =>
            apiFetch<{ ok: true; updated: boolean }>(`/v1/web/accounts/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
        onSuccess: () => invalidateReferences(qc),
    });
}

/**
 * Удаление мягкое. Без force сервер отвечает 409 и присылает разбивку по таблицам —
 * страница показывает её и переспрашивает, вместо того чтобы молча осиротить историю.
 */
export function useDeleteAccount() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
            apiFetch<{ ok: true; deleted: boolean; usage: AccountUsage }>(
                `/v1/web/accounts/${id}${force ? "?force=1" : ""}`,
                { method: "DELETE" },
            ),
        onSuccess: () => invalidateReferences(qc),
    });
}

// ── Чистка комментариев к тратам (SPEC-055) ────────────────────────────────

export function useNoteCleanupCandidates(includeDismissed: boolean) {
    return useQuery({
        queryKey: ["note-cleanup-candidates", includeDismissed],
        queryFn: () => apiFetch<NoteCleanupCandidates>(
            `/v1/web/note-cleanup/candidates${includeDismissed ? "?include_dismissed=1" : ""}`),
        staleTime: 60_000,
    });
}

export function useNoteCleanupRuns() {
    return useQuery({
        queryKey: ["note-cleanup-runs"],
        queryFn: () => apiFetch<NoteCleanupRunsResponse>("/v1/web/note-cleanup/runs"),
        staleTime: 30_000,
    });
}

/** После применения инвалидируем и расходы: их note изменились. */
function invalidateCleanup(qc: ReturnType<typeof useQueryClient>) {
    qc.invalidateQueries({ queryKey: ["note-cleanup-candidates"] });
    qc.invalidateQueries({ queryKey: ["note-cleanup-runs"] });
    qc.invalidateQueries({ queryKey: ["expenses"] });
}

export function useNoteCleanupApply() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: unknown) => apiFetch<NoteCleanupApplyResult>("/v1/web/note-cleanup/apply", {
            method: "POST", body: JSON.stringify(payload),
        }),
        onSuccess: () => invalidateCleanup(qc),
    });
}

export function useNoteCleanupRevert() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (runId: string) => apiFetch<{ reverted: number; conflicts: number }>(
            `/v1/web/note-cleanup/runs/${runId}/revert`, { method: "POST" }),
        onSuccess: () => invalidateCleanup(qc),
    });
}

export function useNoteCleanupUndismiss() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (pair: { source: string; target: string }) => apiFetch<{ deleted: number }>(
            "/v1/web/note-cleanup/decisions", { method: "DELETE", body: JSON.stringify(pair) }),
        onSuccess: () => invalidateCleanup(qc),
    });
}
